import { ROUTES } from '../../routing';
import { useHistory, useParams } from 'react-router-dom';
import { TeamsProfilePayload } from '../../api/teams';
import { useTheme } from 'styled-components';
import styled, { css } from 'styled-components';
import { H3, PSmall } from '../elements/Text';
import dayjs from 'dayjs';

export default function CohortMilestones({
  onboardedCohort,
}: {
  onboardedCohort:
    | TeamsProfilePayload['onboardedCohorts'][number]
    | null
    | undefined;
}) {
  const theme = useTheme();
  const history = useHistory();
  const { companySlug } = useParams<{ companySlug: string | undefined }>();

  if (!onboardedCohort || !onboardedCohort.milestones) {
    return null;
  }

  const milestones = [
    ...(onboardedCohort.milestones.PROJECT_CHECKIN_1
      ? [
          {
            key: 'checkin1',
            title: 'Company Project Check-in #1',
            desc: 'Log your initial company project ideas.',
            unlockWeek: 1,
            isUnlocked: !onboardedCohort.milestones.PROJECT_CHECKIN_1.isLocked,
            isSubmitted:
              onboardedCohort.milestones.PROJECT_CHECKIN_1.submittedAt !== null,
            ctaLabel: 'Log Ideas',
            faIcon: 'fa-duotone fa-clipboard-list',
            onClick: () => {
              history.push(
                ROUTES.TEAMS.COMPANY_PROJECT_CHECKIN_1.getPath({
                  teamSlug: companySlug,
                })
              );
            },
          },
        ]
      : []),
    ...(onboardedCohort.milestones.PROJECT_CHECKIN_2
      ? [
          {
            key: 'checkin2',
            title: 'Company Project Check-in #2',
            desc: 'Confirm your chosen company project direction.',
            unlockWeek: 2,
            isUnlocked: !onboardedCohort.milestones.PROJECT_CHECKIN_2.isLocked,
            isSubmitted:
              onboardedCohort.milestones.PROJECT_CHECKIN_2.submittedAt !== null,
            ctaLabel: 'Confirm Project',
            faIcon: 'fa-duotone fa-clipboard-list-check',
            onClick: () => {
              history.push(
                ROUTES.TEAMS.COMPANY_PROJECT_CHECKIN_2.getPath({
                  teamSlug: companySlug,
                })
              );
            },
          },
        ]
      : []),
    ...(onboardedCohort.milestones.PROJECT_UPLOAD
      ? [
          {
            key: 'submitProject',
            title: 'Submit Company Project',
            desc: 'Share what you did with your company project.',
            unlockWeek: null,
            isUnlocked: !onboardedCohort.milestones.PROJECT_UPLOAD.isLocked,
            isSubmitted:
              onboardedCohort.milestones.PROJECT_UPLOAD.submittedAt !== null,
            ctaLabel: 'Submit',
            faIcon: 'fa-duotone fa-upload',
            onClick: () => {
              history.push(
                ROUTES.TEAMS.COMPANY_PROJECT_SUBMIT.getPath({
                  teamSlug: companySlug,
                })
              );
            },
          },
        ]
      : []),
    ...(onboardedCohort.milestones.COHORT_FEEDBACK
      ? [
          {
            key: 'feedback',
            title: 'Retro Survey',
            desc: 'Share your feedback and how the cohort helped you.',
            unlockWeek: null,
            isUnlocked: !onboardedCohort.milestones.COHORT_FEEDBACK.isLocked,
            isSubmitted:
              onboardedCohort.milestones.COHORT_FEEDBACK.submittedAt !== null,
            ctaLabel: 'Give Feedback',
            faIcon: 'fa-duotone fa-message-smile',
            onClick: () => {
              history.push(
                ROUTES.TEAMS.COHORT_FEEDBACK.getPath({
                  teamSlug: companySlug,
                })
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <CohortDateRow>
        <MilestonesTitle>Cohort Milestones</MilestonesTitle>
        <RowGroup>
          <MilestonesCalendarIcon />
          <MilestonesCohortLabel>
            {dayjs.utc(onboardedCohort.cohort.startDate).format('MMMM D, YYYY')}
            {' - '}
            {dayjs.utc(onboardedCohort.cohort.endDate).format('MMMM D, YYYY')}
          </MilestonesCohortLabel>
        </RowGroup>
      </CohortDateRow>
      <MilestonesSection>
        {milestones.map((m) => (
          <MilestoneRow
            key={m.key}
            isUnlocked={m.isUnlocked}
            isLast={m.key === milestones[milestones.length - 1].key}
            isFirst={m.key === milestones[0].key}
            isCompleted={m.isSubmitted}
            onClick={m.isUnlocked ? m.onClick : undefined}
          >
            {m.isSubmitted ? (
              <MilestoneCheckIcon />
            ) : (
              <MilestoneIcon className={m.faIcon} />
            )}
            <MilestoneInfo>
              <MilestoneTitle>{m.title}</MilestoneTitle>
              <MilestoneDesc>
                {m.desc}
                {!m.isUnlocked && (
                  <span
                    style={{ color: theme.colors.secondary, fontWeight: 500 }}
                  >
                    {' '}
                    {m.unlockWeek
                      ? `Unlocks in week ${m.unlockWeek}.`
                      : 'Unlocks in the final week.'}
                  </span>
                )}
              </MilestoneDesc>
            </MilestoneInfo>
            <MilestoneStatus>
              <MilestonesCTAText isCompleted={m.isSubmitted}>
                {m.ctaLabel}
              </MilestonesCTAText>
              <ArrowRightIcon isCompleted={m.isSubmitted} />
            </MilestoneStatus>
          </MilestoneRow>
        ))}
      </MilestonesSection>
    </>
  );
}

const MilestonesCTAText = styled(PSmall)<{ isCompleted: boolean }>`
  color: ${({ theme, isCompleted }) =>
    !isCompleted ? theme.colors.primary : theme.colors.textRegular};
  font-weight: 600;
  margin-top: 0;
  padding-bottom: 0;
`;

const ArrowRightIcon = styled.i.attrs({
  className: 'fa-solid fa-forward',
})<{ isCompleted: boolean }>`
  color: ${({ theme, isCompleted }) =>
    !isCompleted ? theme.colors.primary : theme.colors.textRegular};
  font-size: 16px;
  margin-top: 0;
  margin-right: 10px;
`;

const MilestonesTitle = styled(H3)`
  margin: 0;
  padding-bottom: 0;
`;

const MilestonesCalendarIcon = styled.i.attrs({
  className: 'fa-duotone fa-calendar-alt',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 16px;
  margin-top: 0;
  padding-bottom: 0;
`;

const MilestonesCohortLabel = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 600;
  margin-top: 0;
  padding-bottom: 0;
`;

const MilestoneIcon = styled.i`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 32px;
  margin-top: 0;
  padding: 0 20px 0;
`;

const MilestoneCheckIcon = styled.i.attrs({
  className: 'fa-regular fa-check-circle',
})`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 32px;
  margin-top: 0;
  padding: 0 20px 0;
`;

const MilestoneInfo = styled.div`
  flex: 1;
`;

const MilestoneTitle = styled(PSmall)`
  font-weight: 700;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const MilestoneDesc = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin-top: 2px;
`;

const MilestoneStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CohortDateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 32px;
  margin-bottom: 18px;
`;

const MilestonesSection = styled.div`
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 12px;
  padding: 0;
  margin: 32px 0;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const RowGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const MilestoneRow = styled.div<{
  isUnlocked: boolean;
  isLast: boolean;
  isFirst: boolean;
  isCompleted: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 18px;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  opacity: ${({ isUnlocked }) => (isUnlocked ? 1 : 0.6)};
  &:last-child {
    border-bottom: none;
  }

  transition: all 0.3s ease-in-out;

  ${({ isUnlocked, isCompleted }) =>
    isUnlocked &&
    css`
      &:hover {
        cursor: pointer;
        background-color: ${({ theme }) => theme.colors.bgActive};

        ${isCompleted &&
        css`
          background-color: ${({ theme }) => theme.colors.primaryBg};
          filter: brightness(1.2);
        `}
      }
    `}

  ${({ isFirst }) =>
    isFirst &&
    css`
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    `}

  ${({ isLast }) =>
    isLast &&
    css`
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    `}

  ${({ isCompleted, theme }) =>
    isCompleted &&
    css`
      background: ${theme.colors.primaryBg}; // green transparent
    `}
`;
